/**
 * Iter269.20w — Floating "Back to top" button.
 *
 * Appears in the bottom-right corner once the visitor has scrolled
 * past `threshold` pixels (default 1500). Smooth-scrolls back to the
 * top of the document on click. Auto-hides on short pages.
 *
 * Stacked just above the Qelli avatar (`bottom: 200px`) so it never
 * overlaps the chat launcher. Hidden inside <QorTraceOnlyChrome>, so
 * it's automatically suppressed on /qorbom/* and /deck/* surfaces.
 */
import React, { useEffect, useState, useCallback } from "react";
import { ArrowUp } from "lucide-react";

const THRESHOLD = 1500;

export default function BackToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > THRESHOLD);
    onScroll();                                   // initial
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = useCallback(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    } catch {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="back-to-top-btn"
      aria-label="Back to top"
      title="Back to top"
      style={{
        position:        "fixed",
        right:           18,
        bottom:          200,        // just above the Qelli avatar
        zIndex:          70,
        width:           44,
        height:          44,
        padding:         0,
        borderRadius:    "50%",
        border:          "1px solid rgba(0,229,255,0.45)",
        background:      "rgba(11,19,36,0.85)",
        color:           "var(--qt-cyan)",
        backdropFilter:  "blur(8px)",
        boxShadow:       "0 4px 16px rgba(0, 229, 255, 0.18), 0 0 0 1px rgba(255,255,255,0.04) inset",
        cursor:          "pointer",
        opacity:         visible ? 1 : 0,
        transform:       visible ? "translateY(0) scale(1)" : "translateY(12px) scale(0.85)",
        pointerEvents:   visible ? "auto" : "none",
        transition:      "opacity 220ms ease, transform 220ms ease",
        display:         "inline-flex",
        alignItems:      "center",
        justifyContent:  "center",
      }}>
      <ArrowUp size={18} strokeWidth={2.25} />
    </button>
  );
}
